/**
 * "Minha loja" — o estado de filial do app, e a decisao de produto que ele carrega.
 *
 * ## Por que isto saiu de `app/sessao.tsx`
 *
 * `storeId` morava no contexto de sessao e nunca era definido por ninguem: `definirLoja` nao
 * tinha um unico chamador em todo o app. O efeito era o defeito que esta rodada conserta — a
 * busca ficava permanentemente desabilitada e a tela dizia "Nada encontrado". Mais de fundo: a
 * filial **nao e sessao**. Ela sobrevive ao logout de proposito (`PWA §7.2`, `kv` com
 * `limpaNoLogout: false`), e um `ProvedorDeSessao` que guardasse algo que a sessao nao apaga
 * convida exatamente ao bug oposto — apagar a preferencia do aparelho ao sair da conta.
 *
 * ## A estrategia de selecao, e por que esta
 *
 * `PWA §10.2` e normativo e fecha a questao antes do gosto:
 *
 * > *"**Recomendacao: nao usar [geolocalizacao] no MVP.** [...] O que entra no lugar:
 * > 1. **Selecao manual de filial**, persistida em `kv.storeId`, com 'trocar de loja' sempre
 * > visivel no cabecalho."*
 *
 * Disso saem tres regras, e uma quarta que o documento nao decide e esta escrita aqui:
 *
 * 1. **Manual.** Nao adivinhamos por localizacao (o documento recusa) e **nao adivinhamos pela
 *    primeira filial aberta**. Preco, estoque e catalogo mudam por filial (§45, C-01): escolher
 *    por conta propria e mostrar o preco da Filial A para quem compraria na Filial B, e essa e
 *    a classe de erro que o C-01 trata como bloqueante. Um default silencioso tambem seria
 *    invisivel — a pessoa so descobriria a filial errada no balcao.
 * 2. **Lembrada.** Sobrevive a recarga e ao logout (`preferencia.ts`).
 * 3. **Trocavel a qualquer momento**, pelo cabecalho (`DESIGN_SYSTEM §7.2`).
 * 4. **Uma filial so nao e uma escolha.** Quando `GET /v1/stores` devolve exatamente uma
 *    filial, ela e selecionada sozinha. O produto e de **instancia dedicada** (ADR-0019) e ha
 *    cliente com uma unica loja; fazer uma pergunta de resposta unica e atrito puro, e nao ha
 *    filial errada possivel. Com duas ou mais, o app **pergunta** — e nao bloqueia: o cabecalho
 *    mostra "Escolher loja" e cada tela que depende de filial mostra o estado que diz o que
 *    falta e o que fazer.
 *
 * O que **nao** fazemos: abrir a folha de escolha sozinha no boot. Uma camada modal por cima do
 * primeiro quadro, antes de a pessoa ter visto o app, e o padrao que o publico-alvo fecha no
 * reflexo — e quem usa leitor de tela perde o contexto da pagina logo na entrada.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  lerLojaPreferida,
  lembrarLojaPreferida,
  reidratarLojaPreferida,
  type LojaPreferida,
  type PortasDaLoja,
} from './preferencia.js';
import { portasDaLojaDoNavegador } from './portas.js';

export interface MinhaLoja {
  /** A filial escolhida, com o nome de vitrine para o cabecalho. `null` = ainda nao escolheu. */
  readonly loja: LojaPreferida | null;
  /** Atalho: o `storeId` que as rotas exigem. `null` **impede** a requisicao, nunca a permite. */
  readonly storeId: string | null;
  /**
   * Ainda lendo a copia duravel do IndexedDB.
   *
   * A tela usa isto para **nao** anunciar "escolha uma loja" no primeiro quadro de quem ja
   * escolheu e acabou de sair da conta (o espelho sincrono foi apagado pela purga, o `kv` nao).
   */
  readonly hidratando: boolean;
  readonly escolher: (loja: LojaPreferida) => void;
  readonly seletorAberto: boolean;
  readonly abrirSeletor: () => void;
  readonly fecharSeletor: () => void;
}

const ContextoDeLoja = createContext<MinhaLoja | null>(null);

export interface ProvedorDeMinhaLojaProps {
  readonly children: ReactNode;
  /**
   * Injetavel para o teste. `undefined` (o caso do app) le o espelho sincrono do
   * `localStorage`; `null` explicito significa "nenhuma filial", que e o estado da primeira
   * visita e o que o teste de regressao desta rodada precisa poder montar.
   */
  readonly lojaInicial?: LojaPreferida | string | null;
  readonly portas?: PortasDaLoja;
}

function normalizar(valor: LojaPreferida | string | null): LojaPreferida | null {
  if (valor === null) return null;
  return typeof valor === 'string' ? { id: valor, nome: '' } : valor;
}

export function ProvedorDeMinhaLoja({
  children,
  lojaInicial,
  portas,
}: ProvedorDeMinhaLojaProps): ReactNode {
  const [loja, setLoja] = useState<LojaPreferida | null>(() =>
    lojaInicial === undefined ? lerLojaPreferida() : normalizar(lojaInicial),
  );
  const [seletorAberto, setSeletorAberto] = useState(false);
  // Comeca hidratando so quando nao ha nada no espelho: com filial em maos, nao ha o que esperar.
  const [hidratando, setHidratando] = useState(() => loja === null);

  const portasEfetivas = useMemo<PortasDaLoja>(
    () => portas ?? portasDaLojaDoNavegador(),
    [portas],
  );

  useEffect(() => {
    if (!hidratando) return undefined;
    let vivo = true;
    void reidratarLojaPreferida(portasEfetivas).then((doBanco) => {
      if (!vivo) return;
      if (doBanco !== null) setLoja(doBanco);
      setHidratando(false);
    });
    return () => {
      vivo = false;
    };
    // `hidratando` esta nas dependencias de proposito, e nao suprimido: ele so vai de `true`
    // para `false`, entao a reexecucao que ele provoca cai no `return` da primeira linha. Uma
    // dependencia honesta que executa uma vez e melhor que uma lista incompleta com um
    // `eslint-disable` por cima — sobretudo porque a regra `exhaustive-deps` nao esta ligada
    // neste repositorio, e o comentario de supressao seria uma protecao imaginaria.
  }, [portasEfetivas, hidratando]);

  /**
   * Grava a escolha. **Nao fecha a folha** — e quem chama que decide.
   *
   * Ate esta rodada, `escolher` fechava o seletor sozinho, e isso era conveniente enquanto a
   * escolha era o fim da historia. Deixou de ser: com carrinho cheio, trocar de filial revalida
   * as linhas e **informa perdas** (`DOMAIN` INV-C1 e RN-076), e essa lista aparece num passo da
   * propria folha — que precisa continuar aberta depois de a filial mudar. Um `escolher` que
   * fechasse a camada tornaria o passo de resumo irrepresentavel, e a alternativa (reabrir a
   * folha em seguida) piscaria a camada inteira na cara de quem esta lendo o que perdeu.
   */
  const escolher = useCallback(
    (nova: LojaPreferida) => {
      setLoja(nova);
      setHidratando(false);
      void lembrarLojaPreferida(nova, portasEfetivas);
    },
    [portasEfetivas],
  );

  const abrirSeletor = useCallback(() => {
    setSeletorAberto(true);
  }, []);
  const fecharSeletor = useCallback(() => {
    setSeletorAberto(false);
  }, []);

  const valor = useMemo<MinhaLoja>(
    () => ({
      loja,
      storeId: loja?.id ?? null,
      hidratando,
      escolher,
      seletorAberto,
      abrirSeletor,
      fecharSeletor,
    }),
    [loja, hidratando, escolher, seletorAberto, abrirSeletor, fecharSeletor],
  );

  return <ContextoDeLoja.Provider value={valor}>{children}</ContextoDeLoja.Provider>;
}

export function useMinhaLoja(): MinhaLoja {
  const valor = useContext(ContextoDeLoja);
  if (valor === null) {
    throw new Error('Use <ProvedorDeMinhaLoja> acima do componente que consome a filial.');
  }
  return valor;
}
